import type { Question, GenerationParams } from '../../../../services/types';
import { HeaderSettings } from '../../Headerfiles/types';
import { generatePdfHeader, getFontImports } from '../../Headerfiles/pdfHeaderGenerator';

const getPrintableStyles = () => `
    <style>
        @import url('https://fonts.googleapis.com/css2?family=Times+New+Roman&display=swap');
        body { 
            font-family: 'Times New Roman', Times, serif; 
            font-size: 11pt;
            line-height: 1.3;
            color: #333;
            margin: 0;
            padding: 30px;
        }
        .header { text-align: center; border-bottom: 2px double #333; padding-bottom: 8px; margin-bottom: 15px; }
        .header h1 { font-size: 16pt; margin: 0; }
        .header .details { display: flex; justify-content: space-between; font-size: 10pt; margin-top: 8px; padding: 0 15px; }
        
        .section-header { margin-top: 25px; margin-bottom: 10px; }
        .section-header h2 { font-size: 14pt; margin: 0; padding-bottom: 4px; border-bottom: 1px solid #333; }

        .question-container { margin-bottom: 12px; page-break-inside: avoid; }
        .question-text { white-space: pre-wrap; margin-bottom: 5px; }
        .question-text svg { max-width: 100%; height: auto; }
        
        .answer-container { margin-top: 8px; border-left: 2px solid #e0e0e0; padding-left: 12px; }
        .answer-container h4 { font-size: 10pt; font-weight: bold; margin: 0 0 4px 0; color: #0056b3; }
        .answer-text {
            white-space: pre-wrap;
            color: #006400; 
            background-color: #f8f9fa; 
            padding: 8px; 
            border-radius: 4px;
            font-size: 10pt;
        }
        .options-list { margin-top: 5px; margin-left: 20px; }
        .option-item { white-space: pre-wrap; margin-bottom: 3px; }
    </style>
`;

const createPrintableContent = (params: GenerationParams, questions: Question[], includeAns: boolean, headerSettings?: HeaderSettings) => {
  const questionsBySection = questions.reduce((acc, q) => {
    const sectionName = q.section || 'Uncategorized';
    if (!acc[sectionName]) {
      acc[sectionName] = [];
    }
    acc[sectionName].push(q);
    return acc;
  }, {} as Record<string, Question[]>);

  const examTypeDisplay = params.examType === 'Custom' ? '' : params.examType;

  // Use centralized header generator
  const customHeader = generatePdfHeader(headerSettings);
  let headerHTML = '';

  if (customHeader) {
    // Custom header with exam info below it
    // Note: margin-bottom is controlled by the header's separator.marginBottom setting
    headerHTML = customHeader + `
            <div style="text-align: center;">
                <h2 style="font-size: 16pt; margin: 0;">${params.examTitle || `${params.subject} Exam`}</h2>
                <div style="display: flex; justify-content: center; gap: 20px; font-size: 10pt; margin-top: 5px;">
                    <span><strong>Type:</strong> ${examTypeDisplay}</span>
                    <span><strong>Topic(s):</strong> ${params.topics.join(', ')}</span>
                    <span><strong>Total Marks:</strong> ${params.totalMarks}</span>
                </div>
            </div>
        `;
  } else {
    // Default header (no custom header)
    headerHTML = `
            <div class="header">
                <h1>${params.examTitle || `${params.subject} Exam`}</h1>
                <div class="details">
                    <span><strong>Type:</strong> ${examTypeDisplay}</span>
                    <span><strong>Topic(s):</strong> ${params.topics.join(', ')}</span>
                    <span><strong>Total Marks:</strong> ${params.totalMarks}</span>
                </div>
            </div>
        `;
  }

  let content = headerHTML;

  Object.keys(questionsBySection).sort().forEach(sectionName => {
    const sectionConfig = params.sections.find(s => s.name === sectionName);
    content += `
            <div class="section-header">
                <h2>${sectionName.toUpperCase()} ${sectionConfig ? `(${sectionConfig.marks} Marks)` : ''}</h2>
            </div>
        `;
    questionsBySection[sectionName].forEach((q, index) => {
      const cleanedQuestion = q.question.replace(/^\d+\.\s*/, '').trim();
      content += `
                <div class="question-container">
                    <div class="question-text">${index + 1}. ${cleanedQuestion}</div>
            `;
      if (q.type === 'MCQ' && Array.isArray((q as any).options) && (q as any).options.length > 0) {
        content += `
                    <div class="options-list">
                        ${(q as any).options
            .map((option: string, optIndex: number) => `<div class="option-item">${String.fromCharCode(65 + optIndex)}. ${option}</div>`)
            .join('')}
                    </div>
                `;
      }
      if (includeAns) {
        content += `
                    <div class="answer-container">
                        <h4>Answer/Marking Scheme:</h4>
                        <div class="answer-text">${q.answer}</div>
                    </div>
                `;
      }
      content += `</div>`;
    });
  });

  return content;
};

export const exportPDF = (params: GenerationParams, questions: Question[], includeAnswers: boolean, headerSettings?: HeaderSettings) => {
  const content = createPrintableContent(params, questions, includeAnswers, headerSettings);
  const styles = getPrintableStyles();
  const fontImports = getFontImports(headerSettings);

  const printWindow = window.open('', '', 'height=800,width=800');
  if (!printWindow) {
    alert('Please allow popups to export as PDF');
    return;
  }

  printWindow.document.write(`<html><head><title>Export Exam</title>${styles}${fontImports}</head><body>${content}</body></html>`);
  printWindow.document.close();

  // Wait for content (including images) to load before printing
  const hasLogo = headerSettings?.isEnabled && headerSettings.logo.url;
  setTimeout(() => {
    printWindow.print();
  }, hasLogo ? 1500 : 500);
};

export const exportWord = (params: GenerationParams, questions: Question[], includeAnswers: boolean, headerSettings?: HeaderSettings) => {
  const content = createPrintableContent(params, questions, includeAnswers, headerSettings);
  const styles = getPrintableStyles();
  const header = `<html xmlns:o='urn:schemas-microsoft-com:office:office' xmlns:w='urn:schemas-microsoft-com:office:word' xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'><title>Export Exam to Word</title>${styles}</head><body>`;
  const footer = "</body></html>";
  const sourceHTML = header + content + footer;

  const source = 'data:application/vnd.ms-word;charset=utf-8,' + encodeURIComponent(sourceHTML);
  const fileDownload = document.createElement("a");
  document.body.appendChild(fileDownload);
  fileDownload.href = source;
  fileDownload.download = `exam-${params.subject}-${params.topics.join('-').replace(/\s+/g, '-')}.doc`;
  fileDownload.click();
  document.body.removeChild(fileDownload);
};